import { createApi, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { transformResponses } from 'src/app-redux/rtkq.interceptor';
import { apiResources } from 'src/common/Constants';
import { getLoggedInUser } from 'src/management/User';
import type { City, GoodsType, LookupObject, VehicleVariant } from 'src/shipment/Lookup';
import { baseQueryWithAuth } from '../rtkq.interceptor';

const lookupsTag = 'lookups';

/** Filters vehicle types based on user preferences. */
function filterVehicleTypes(vehicleTypes: Array<LookupObject>): Array<LookupObject> {
  const CONFIG_TYPES = getLoggedInUser().preferences?.vehicleTypes;
  if (CONFIG_TYPES?.length) return vehicleTypes?.filter((v) => CONFIG_TYPES.includes(v.id));
  else return vehicleTypes;
}

/** RTK Query API slice for lookup-related endpoints. */
export const lookupsApi = createApi({
  reducerPath: 'lookupsApi',
  tagTypes: [lookupsTag],
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /** Query to fetch all cities. */
    fetchCities: builder.query<Array<City>, void>({
      query: () => apiResources.city,
      ...transformResponses,
    }),
    /** Query to fetch cities by search term (minimum 3 characters). */
    fetchCitiesByTerm: builder.query<Array<City> | undefined, string>({
      queryFn: async (keyword, _api, _options, fetchWithBQ) => {
        if (keyword.length < 3) return {}; // Filter expensive API calls

        const { data, error } = await fetchWithBQ(`${apiResources.endpoint}/city/search/${keyword}`);
        if (error != null) return { error: error.data as FetchBaseQueryError };
        return { data: transformResponses.transformResponse(data as any) };
      },
    }),
    /** Query to fetch all countries. */
    fetchCountry: builder.query<Array<LookupObject>, void>({
      query: () => `${apiResources.endpoint}/country`,
      ...transformResponses,
    }),
    /** Query to fetch all countries (alternate endpoint). */
    fetchAllCountry: builder.query<Array<LookupObject>, void>({
      query: () => apiResources.allCountries,
      ...transformResponses,
    }),
    /** Query to fetch all states for a given country ID. */
    fetchStates: builder.query<Array<LookupObject>, number>({
      query: (id) => `${apiResources.endpoint}/country-state?countryId=${id}`,
      ...transformResponses,
    }),
    /** Query to fetch all cities for a given state ID. */
    fetchStateCities: builder.query<Array<LookupObject>, number>({
      query: (id) => `${apiResources.endpoint}/city?stateId=${id}`,
      ...transformResponses,
    }),
    /** Query to fetch all goods types. */
    fetchGoodTypes: builder.query<Array<GoodsType>, void>({
      query: () => apiResources.goodType,
      ...transformResponses,
    }),
    /** Query to fetch all vehicle types, optionally filtered by user preferences. */
    fetchVehicleTypes: builder.query<Array<LookupObject>, { all: boolean } | void>({
      queryFn: async (args, _api, _options, fetchWithBQ) => {
        const { data, error } = await fetchWithBQ(apiResources.vehicleType);
        if (error != null) return { error: error.data as FetchBaseQueryError };
        const vehicleTypes = transformResponses.transformResponse<Array<LookupObject>>(data as any) || [];
        return { data: args?.all ? vehicleTypes : filterVehicleTypes(vehicleTypes) };
      },
    }),
    /** Query to fetch all vehicle variants for given vehicle types. */
    fetchVehicleVariants: builder.query<Array<VehicleVariant>, Array<LookupObject> | void>({
      queryFn: async (vehicleTypes, { dispatch }, _extraOptions, fetchWithBQ) => {
        if (!vehicleTypes) {
          const { data: allTypes } = await dispatch(lookupsApi.endpoints.fetchVehicleTypes.initiate());
          if (!allTypes?.length)
            return { error: { error: 'Unable to fetch vehicle types.', status: 404, data: undefined } };
          vehicleTypes = allTypes;
        }
        const results = await Promise.all(
          vehicleTypes.map(({ id }) => fetchWithBQ(`${apiResources.vehicleVariant}/${id}?page=0`)),
        );
        const merged: Array<VehicleVariant> = [].concat(
          ...results.map(({ data }) => transformResponses.transformResponse(data as any) as any),
        );
        const errors: Array<FetchBaseQueryError> = [].concat(
          ...results.filter(({ error }) => error != null).map(({ error }: any) => error.data),
        );
        if (errors.length > 0) return { error: errors[0] };
        return { data: merged };
      },
    }),
  }),
});

/** RTK Query hooks for lookup-related endpoints. */
export const {
  useLazyFetchCitiesByTermQuery,
  useFetchCountryQuery,
  useFetchAllCountryQuery,
  useLazyFetchStatesQuery,
  useLazyFetchStateCitiesQuery,
  useFetchGoodTypesQuery,
  useLazyFetchGoodTypesQuery,
  useFetchVehicleTypesQuery,
  useLazyFetchVehicleTypesQuery,
  useFetchVehicleVariantsQuery,
} = lookupsApi;

/**
 * @deprecated use `useLazyFetchCitiesByTermQuery()` instead.
 * Query to fetch all cities (deprecated).
 */
export const useFetchCitiesQuery = lookupsApi.useFetchCitiesQuery;
